import { ReactNode, useCallback } from "react";

import { LOGIN_POLICY_CREATION_PROMPT } from "components/AssistantPanel/prompts";
import { InfoCircle } from "components/icons/generated";
import ListContentWrapper from "components/ListContentWrapper";
import Badge from "ds/components/Badge";
import Box from "ds/components/Box";
import LinkButton from "ds/components/Button/LinkButton";
import ButtonIcon from "ds/components/ButtonIcon";
import Callout from "ds/components/Callout";
import FeedbackActions from "ds/components/Feedback/Actions";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import useTypedContext from "hooks/useTypedContext";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";
import PageInfoHeaderTitle from "ds/components/PageInfoHeader/Title";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";
import PoliciesListCreatePolicyButton from "views/Account/Policies/CreatePolicyButton";

import { SettingsContext } from "../Context";
import { getManagementStrategy } from "../helpers";
import SeatLimitsNotificationLoginPolicies from "../SeatLimitsNotification/LoginPolicies";
import { UserManagementActivationStatus } from "../types";
import { LOGIN_POLICY_INACTIVE_INFO_STORAGE_KEY } from "./constants";
import { showLoginPoliciesCreateDrawer } from "./CreateDrawer";
import { showLoginPoliciesInfoDrawer } from "./InfoDrawer";

type LoginPoliciesPageLayoutProps = {
  children: ReactNode;
  takenPolicyNames: string[];
};

const LoginPoliciesPageLayout = ({ children, takenPolicyNames }: LoginPoliciesPageLayoutProps) => {
  const { activationStatus } = useTypedContext(SettingsContext);

  const isLoginPolicyActive = activationStatus === UserManagementActivationStatus.INACTIVE;

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageOrganization.OrganizationLoginPolicies,

    defaultCallbackTrackProperties: {
      managementStrategy: getManagementStrategy(activationStatus),
    },
  });

  const openInfoDrawer = useCallback(() => {
    trackSegmentAnalyticsEvent("Info Click");
    showLoginPoliciesInfoDrawer();
  }, [trackSegmentAnalyticsEvent]);

  const openCreateDrawer = useCallback(() => {
    trackSegmentAnalyticsEvent("Create Policy Start");
    showLoginPoliciesCreateDrawer({
      takenPolicyNames,
    });
  }, [takenPolicyNames, trackSegmentAnalyticsEvent]);

  return (
    <>
      {!isLoginPolicyActive && (
        <Callout
          title="Login policy is inactive"
          variant="warning"
          storageKey={LOGIN_POLICY_INACTIVE_INFO_STORAGE_KEY}
        >
          Login policies are not evaluated when Identity Access Management is active. Change
          management strategy to use login policy.
          <FeedbackActions>
            <LinkButton to="/settings/management-strategy/" variant="contrast" size="small">
              Choose strategy
            </LinkButton>
          </FeedbackActions>
        </Callout>
      )}
      <SeatLimitsNotificationLoginPolicies />
      <PageInfoHeaderWrapper>
        <Box align="center" __deprecatedGap="1.1rem">
          <PageInfoHeaderTitle title="Login policy" />
          <ButtonIcon onPress={openInfoDrawer} icon={InfoCircle} variant="ghost">
            Open info drawer
          </ButtonIcon>
          <Badge
            variant={isLoginPolicyActive ? "green" : "gray"}
            type="strong"
            text={isLoginPolicyActive ? "ACTIVE" : "INACTIVE"}
          />
        </Box>
        <PageInfoHeaderActions>
          {openCreateDrawer && (
            <PoliciesListCreatePolicyButton
              onCreateManually={openCreateDrawer}
              prompt={LOGIN_POLICY_CREATION_PROMPT}
              entryPoint="login_policy_creation"
              analyticsPage={AnalyticsPageOrganization.OrganizationLoginPolicies}
            />
          )}
        </PageInfoHeaderActions>
      </PageInfoHeaderWrapper>
      <ListContentWrapper>{children}</ListContentWrapper>
    </>
  );
};

export default LoginPoliciesPageLayout;
